<template lang="pug">
  v-card.overflow-hidden
    v-card-title
      h2.modal-title {{ 'base.report' | translate }}
    table.table
      thead
        tr.table__row.table__row--head
          th.table__cell.nowrap {{ 'base.groups' | translate }}
          th.table__cell.text-right {{ 'base.animal_count' | translate }}
          th.table__cell.nowrap.text-right {{ 'base.weight' | translate }} [{{ 'base.kg' | translate }}]#[br] {{ 'base.plan_fact' | translate }}
          th.table__cell.nowrap.text-right {{ 'base.deviation' | translate }} [{{ 'base.kg' | translate }} / %]
          th.table__cell.nowrap.text-right {{ 'base.weight' | translate }} [{{ 'base.kg' | translate }}/{{ 'base.heads' | translate }}]#[br]{{ 'base.plan_fact' | translate }}
          th.table__cell.nowrap.text-right {{ 'base.dry_metter' | translate }} [{{ 'base.kg' | translate }}/{{ 'base.heads' | translate }}]#[br] {{ 'base.plan_fact' | translate }}
          th.table__cell.text-right {{ 'base.uploading_start' | translate }}
          th.table__cell.text-right {{ 'base.uploading_time' | translate }}
          th.table__cell.nowrap.text-right {{ 'base.operator' | translate }}
      tbody
        tr.table__row(
          v-for="item in scheduleGroups"
          :key="item.id")
          td.table__cell.textwrap
            .table-name {{ item.name }}
          td.table__cell.nowrap.text-right {{ item.animal_count }}
          td.table__cell.nowrap.text-right
            span(:class="allowViewMonitoringGroup ? getGroupColorClass(item) : ''") {{ formateWeightLabel(item) }}
          td.table__cell.nowrap.text-right {{ formatePercentDiffLabel(item) }}
          td.table__cell.nowrap.text-right {{ formateWeightPerHeadLabel(item) }}
          td.table__cell.nowrap.text-right {{ formateDryWeightLabel(item) }}
          td.table__cell.nowrap.text-right {{ formateTimeString(item) }}
          td.table__cell.nowrap.text-right {{ formateTimeloadLabel(item) }}
          td.table__cell.nowrap.text-right {{ formateOperatorLabel(item) }}
        tr.table__row
          td.table__cell.bg-gray {{ 'base.total' | translate }}:
          td.table__cell.bg-gray.text-right {{ total.animal_count }}
          td.table__cell.bg-gray.text-right.nowrap
            span(:class="getTotalColorClass()") {{ formateWeightLabel(total) }}
          td.table__cell.bg-gray.text-right {{ total.diff_weight }}
          td.table__cell.bg-gray.text-right {{ total.plan_weight_per_head + ' / ' + total.fact_weight_per_head }}
          td.table__cell.bg-gray.text-right {{ averageTotal(total.dry_weight_plan) + ' / ' + averageTotal(total.dry_weight_fact) }}
          td.table__cell.bg-gray.text-right {{ formateTimeloadLabel(total) }}
          td.table__cell.bg-gray.text-right
          td.table__cell.bg-gray.text-right
</template>

<script>
import { timeString } from '@/util'
import api from '@/api'

export default {
  name: 'MixerUploadsDetail',

  props: {
    selected: Object
  },

  async mounted () {
    this.calculateTotal()

    if (this.allowViewMonitoringGroup || this.allowViewMonitoringFarm) {
      await this.loadMonitoringSettings()
      if (this.allowViewMonitoringGroup) {
        await this.loadGroupsSettings()
      }
      this.$forceUpdate()
    }
  },

  data: () => ({
    total: {
      animal_count: 0,
      plan_weight: 0,
      fact_weight: 0,
      diff_weight: 0,
      plan_weight_per_head: 0,
      fact_weight_per_head: 0,
      dry_weight_plan: 0,
      dry_weight_fact: 0,
      time_load: 0
    },
    monitoringSettings: null,
    groupsSettings: {}
  }),

  computed: {
    farmId () {
      return this.$route.params.farm_id
    },

    scheduleGroups () {
      if (this.selected) {
        return this.selected.schedule_groups
      }
      return []
    },

    allowViewMonitoringGroup () {
      return this.hasPermission('monitoring_group', 'view')
    },

    allowViewMonitoringFarm () {
      return this.hasPermission('monitoring_farm', 'view')
    }
  },

  methods: {
    async loadMonitoringSettings () {
      if (!this.farmId) {
        return
      }

      try {
        const response = await api.monitoring.farms(this.farmId)
        this.monitoringSettings = response.data
      } catch (error) {
        console.error('Error loading monitoring settings:', error)
      }
    },

    async loadGroupsSettings () {
      if (!this.farmId) {
        return
      }

      try {
        const response = await api.monitoring.groups({ farm: this.farmId })

        if (response.data && Array.isArray(response.data)) {
          const newSettings = {}

          response.data.forEach(setting => {
            if (setting.group && setting.group.id) {
              newSettings[setting.group.id] = setting
            }
          })

          this.groupsSettings = newSettings
        }
      } catch (error) {
        console.error('Error loading groups:', error)
      }
    },

    getGroupColorClass (item) {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      const groupSettings = this.groupsSettings[item.group]

      if (!groupSettings || groupSettings.level === null) {
        return ''
      }

      // Skip only if fact_weight is null (no data recorded)
      // If fact_weight is 0, we should still calculate deviation (100% error)
      if (item.fact_weight === null) {
        return ''
      }

      const deviationPercent = this.calculateDeviationPercent(item)
      const absDeviation = Math.abs(deviationPercent)

      let threshold = 0

      if (groupSettings.level === 'percent') {
        threshold = groupSettings.percent
      } else if (groupSettings.level === 'weight') {
        if (item.plan_weight && item.plan_weight > 0) {
          threshold = (groupSettings.weight / item.plan_weight) * 100
        }
      }

      if (threshold === 0 || threshold === null) {
        return ''
      }

      if (absDeviation > threshold) {
        return 'group-red'
      }

      return ''
    },

    calculateDeviationPercent (item) {
      if (!item.plan_weight || item.plan_weight === 0) {
        return 0
      }

      const deviation = ((item.fact_weight - item.plan_weight) / item.plan_weight) * 100
      return deviation
    },

    formateTimeString (item) {
      if (!item.uploaded_at || !item.time_load) {
        return '-'
      }

      const uploadedDate = new Date(item.uploaded_at)
      const startDate = new Date(uploadedDate.getTime() - (item.time_load * 1000))

      return timeString(startDate.toISOString())
    },

    formateWeightLabel (item) {
      let plan = item.plan_weight ? item.plan_weight.toFixed(0) : '-'
      let fact = item.fact_weight ? item.fact_weight.toFixed(0) : '-'
      return plan + ' / ' + fact
    },

    formateWeightPerHeadLabel (item) {
      let plan = item.plan_weight ? (item.plan_weight / item.animal_count).toFixed(0) : '-'
      let fact = item.fact_weight ? (item.fact_weight / item.animal_count).toFixed(0) : '-'
      return plan + ' / ' + fact
    },

    formatePercentDiffLabel (item) {
      let percent = item.diff_in_percent !== null ? item.diff_in_percent + '%' : '-'
      let weight = item.fact_weight !== null ? Math.round(item.fact_weight - item.plan_weight) : '-'
      return percent + ' / ' + weight
    },

    formateDryWeightLabel (item) {
      const format = num => Math.round(num * 10) / 10
      return format(item.dry_matter_plan) + ' / ' + format(item.dry_matter_fact)
    },

    formateTimeloadLabel (item) {
      return item.time_load ? Math.round(item.time_load / 60) + ' ' + this.$t('base.min') : '-'
    },

    formateOperatorLabel (item) {
      return item.worker !== null ? item.worker : '-'
    },

    averageTotal (total) {
      const groupsCount = this.scheduleGroups.length
      if (groupsCount === 0) return 0
      const rawAvg = total / groupsCount
      return Math.round(rawAvg * 10) / 10
    },

    calculateTotal () {
      for (let item of this.scheduleGroups) {
        this.total.animal_count += item.animal_count ? item.animal_count : 0
        this.total.plan_weight += item.plan_weight ? Math.round(item.plan_weight) : 0
        this.total.fact_weight += item.fact_weight ? Math.round(item.fact_weight) : 0
        this.total.diff_weight += item.fact_weight && item.plan_weight ? Math.round(item.fact_weight - item.plan_weight) : 0
        this.total.plan_weight_per_head += item.plan_weight ? Math.round(item.plan_weight / item.animal_count) : 0
        this.total.fact_weight_per_head += item.fact_weight ? Math.round(item.fact_weight / item.animal_count) : 0
        this.total.dry_weight_plan += item.dry_matter_plan
        this.total.dry_weight_fact += item.dry_matter_fact
        this.total.time_load += item.time_load || 0
      }
    },

    getTotalColorClass () {
      if (!this.allowViewMonitoringFarm) {
        return ''
      }

      if (!this.monitoringSettings || !this.monitoringSettings.upload_weight) {
        return ''
      }

      const generalSettings = this.monitoringSettings.upload_weight

      if (!generalSettings.enabled) {
        return ''
      }

      const planWeight = this.total.plan_weight
      const factWeight = this.total.fact_weight

      // Skip if no plan weight or if fact weight is not recorded (null/undefined)
      // But if factWeight is 0, we should still calculate deviation (100% error)
      if (!planWeight || planWeight === 0) {
        return ''
      }

      if (factWeight === null || factWeight === undefined) {
        return ''
      }

      const deviationPercent = ((factWeight - planWeight) / planWeight) * 100
      const absDeviation = Math.abs(deviationPercent)

      let threshold = null

      if (generalSettings.level === 'percent') {
        threshold = generalSettings.percent
      } else if (generalSettings.level === 'weight' && planWeight > 0) {
        threshold = (generalSettings.weight / planWeight) * 100
      }

      if (threshold !== null && threshold !== undefined && threshold !== 0) {
        if (absDeviation <= threshold) {
          return 'group-green'
        }

        return 'group-red'
      }

      if (generalSettings.green !== 0 && absDeviation <= generalSettings.green) {
        return 'group-green'
      }

      if (generalSettings.red !== 0 && absDeviation >= generalSettings.red) {
        return 'group-red'
      }

      if (generalSettings.yellow !== 0 && absDeviation < generalSettings.yellow) {
        return 'group-yellow'
      }

      return ''
    }
  }
}
</script>
<style scoped>
.v-card__title {
  padding: 2rem 2.4rem 1.6rem;
}

.modal-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
}
</style>

<style scoped>
.table-name {
  max-width: 200px;
  text-overflow: ellipsis;
  overflow: hidden;
}
.table__cell {
  font-size: 1.4rem;
  font-weight: normal;
  padding: 1.8rem 1.3rem;
  background-color: #fff;
}

.group-green {
  background-color: #91EA91 !important;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}

.group-yellow {
  background-color: #FBE268 !important;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}

.group-red {
  background-color: #FF9191 !important;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  display: inline-block;
}
.textwrap {
  max-width: 150px;
  word-break: break-word;
  white-space: unset;
}
</style>